import React from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import { Box, Typography, Button } from '@mui/material';
import Header from "components/Header/Header.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import HomeFooter from "components/Footer/HomeFooter.js";
import Download from "../components/Download";
import SeoServices from "../components/SeoServices";
import SeoLinks from "../components/SeoLinks";
import Error from "./Error";
import { colors } from '../components/Theme/WebTheme';
import { FONT_FAMILY } from "../common/sharedFunctions";
import { getCity, cityMeta, cityJsonLd, SITE_URL } from "../config/seoContent";

const dashboardRoutes = [];

// Pagina de aterrizaje por ciudad para SEO local: "grúa en {ciudad}".
export default function CityLanding(props) {
    const { city: slug } = useParams();
    const city = getCity(slug);
    const { ...rest } = props;

    // Si la ciudad no esta en la lista, mostramos 404 (evita contenido vacio).
    if (!city) {
        return <Error />;
    }

    const { url, title, description } = cityMeta(city);
    const [jsonLd, breadcrumb] = cityJsonLd(city);

    return (
        <div style={{ backgroundColor: colors.LandingPage_Background, margin: '-8px' }}>
            <Helmet>
                <html lang="es" />
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta name="keywords" content={`grúa en ${city.name}, grúa ${city.name}, grúa para carro ${city.name}, grúa para moto ${city.name}, grúas para maquinaria ${city.name}, grúa niñera ${city.name}, grúa cerca de mí, auxilio vial ${city.name}, servicio de grúa 24 horas, Movex`} />
                <link rel="canonical" href={url} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="es_CO" />
                <meta property="og:url" content={url} />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content={`${SITE_URL}/apple-icon.png`} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="geo.region" content="CO" />
                <meta name="geo.placename" content={city.name} />
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
            </Helmet>

            <Header
                color="transparent"
                routes={dashboardRoutes}
                rightLinks={<HeaderLinks />}
                fixed
                changeColorOnScroll={{ height: 400, color: "white" }}
                {...rest}
            />

            <Box
                component="section"
                sx={{
                    backgroundColor: '#000',
                    color: '#fff',
                    pt: { xs: 14, md: 18 },
                    pb: { xs: 6, md: 9 },
                    px: { xs: 3, md: 8 },
                    textAlign: 'center',
                }}
            >
                <Typography component="h1" sx={{ fontFamily: FONT_FAMILY, fontWeight: 'bold', fontSize: { xs: 30, md: 48 }, mb: 2 }}>
                    Grúa en {city.name}
                </Typography>
                <Typography component="p" sx={{ fontFamily: FONT_FAMILY, fontSize: { xs: 16, md: 20 }, color: '#d7d7d7', maxWidth: 880, mx: 'auto', mb: 1.5 }}>
                    Solicita una <strong>grúa en {city.name}</strong> en minutos con Movex: grúa para carro,
                    grúa para moto, grúas para maquinaria y grúa niñera. Precio y tiempo estimado antes de
                    confirmar, conductor verificado y seguimiento en tiempo real.
                </Typography>
                <Typography component="p" sx={{ fontFamily: FONT_FAMILY, fontSize: { xs: 14, md: 16 }, color: '#9e9e9e', maxWidth: 820, mx: 'auto', mb: 4 }}>
                    Servicio de grúa y auxilio vial 24 horas en {city.name} y alrededores ({city.region}).
                </Typography>
                <Button
                    variant="contained"
                    color="warning"
                    href="/login"
                    sx={{ fontFamily: FONT_FAMILY, fontSize: 16, px: 4, py: 1.2 }}
                >
                    Pedir grúa ahora
                </Button>
            </Box>

            <SeoServices />
            <SeoLinks currentCity={city} />
            <Download />
            <HomeFooter />
        </div>
    );
}
